import { useId } from 'react';

import { cn } from '@/lib/cn';

/**
 * A folder, drawn rather than iconified - with papers in it when it has any.
 *
 * Deliberately not part of the icon set in `icon-paths.ts`: those are
 * single-colour 1.5px strokes that inherit `currentColor`, which is right for
 * controls and wrong here. A file explorer is one of the few places where
 * people read an object by its picture before they read its label, so this one
 * is filled and shaded - yellow by default, the way every desktop has drawn a
 * folder for thirty years.
 *
 * Two faces, as Windows draws it: a darker back panel carrying the tab, and a
 * lighter front panel sitting proud of it. Sheets go between the two, so a
 * folder holding something says so before anybody reads its count - which is
 * the whole job of an icon in a grid.
 */

/** The back panel, with the tab that makes it a folder and not a box. */
const BACK =
  'M2 8.5A3.5 3.5 0 0 1 5.5 5h11.8c.93 0 1.82.37 2.47 1.02L23 9.2h19.5A3.5 3.5 0 0 1 46 12.7v20.8a3.5 3.5 0 0 1-3.5 3.5h-37A3.5 3.5 0 0 1 2 33.5z';

const FRONT_SHUT =
  'M5.5 16h37a3.5 3.5 0 0 1 3.5 3.5v14a3.5 3.5 0 0 1-3.5 3.5h-37A3.5 3.5 0 0 1 2 33.5v-14A3.5 3.5 0 0 1 5.5 16z';

/** Open tips the front face forward, which is the whole animation budget. */
const FRONT_OPEN =
  'M6.4 18h39.2a2.8 2.8 0 0 1 2.74 3.4l-2.6 12.4A3.5 3.5 0 0 1 42.3 36.6H5.5A3.5 3.5 0 0 1 2 33.1V21.5A3.5 3.5 0 0 1 5.5 18z';

const LIP_SHUT = 'M5.5 16h37a3.5 3.5 0 0 1 3.5 3.5v.4H2v-.4A3.5 3.5 0 0 1 5.5 16z';
const LIP_OPEN = 'M6.4 18h39.2l-.4 1.9H6.1z';

export function FolderGlyph({
  size = 40,
  empty = false,
  open = false,
  tinted = false,
  className,
}: {
  size?: number;
  /** Greys it out - nothing has been filed here yet. */
  empty?: boolean;
  /** The folder you are inside, with its front face tipped forward. */
  open?: boolean;
  /**
   * Draw it in the current text colour instead of yellow.
   *
   * For folders that already mean a colour - a label's folder is drawn in that
   * label's own, which says what it is without repeating its name beside it.
   * Set the colour on the element, as a class.
   */
  tinted?: boolean;
  className?: string;
}) {
  /* Gradient ids must be unique per instance: a grid of these on one page
     would otherwise all resolve to the first one's stops. */
  const uid = useId().replace(/:/g, '');
  const back = `folder-back-${uid}`;
  const front = `folder-front-${uid}`;

  const paint = empty
    ? { backFrom: '#C9D1DA', backTo: '#AEB8C4', frontFrom: '#E4E9EF', frontTo: '#C4CDD7' }
    : { backFrom: '#F2B53E', backTo: '#DE9A21', frontFrom: '#FFD469', frontTo: '#F6B733' };

  /* Tinted folders shade one colour rather than interpolating two, because
     the colour is the tenant's and only its token is known here. */
  const tint = tinted && !empty;

  return (
    <svg
      viewBox="0 0 48 40"
      width={size}
      height={(size * 40) / 48}
      fill="none"
      className={cn('shrink-0', className)}
      aria-hidden={true}
    >
      {!tint && (
        <defs>
          <linearGradient id={back} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={paint.backFrom} />
            <stop offset="1" stopColor={paint.backTo} />
          </linearGradient>
          <linearGradient id={front} x1="0.1" y1="0" x2="0.9" y2="1">
            <stop offset="0" stopColor={paint.frontFrom} />
            <stop offset="1" stopColor={paint.frontTo} />
          </linearGradient>
        </defs>
      )}

      <path d={BACK} fill={tint ? 'currentColor' : `url(#${back})`} opacity={tint ? 0.7 : 1} />

      {/* A hairline of the back panel's lip, so the two faces separate. */}
      <path d="M2 14.5h44v2.2H2z" fill="#000" opacity="0.07" />

      {/* Papers, sitting between the two faces so they appear to be inside.
          Only when there is something in here: "full" and "empty" should be
          tellable apart across a grid at a glance, before any label is read. */}
      {!empty && (
        <>
          <g transform="rotate(-7 13 18)">
            <rect x="9.5" y="9" width="15" height="12" rx="1.4" fill="#fff" />
            <rect
              x="9.5"
              y="9"
              width="15"
              height="12"
              rx="1.4"
              fill="none"
              stroke="#000"
              strokeOpacity="0.12"
            />
            <path
              d="M12.4 12.6h9.2M12.4 15.4h9.2M12.4 18.2h5.6"
              stroke="#9AA6B2"
              strokeWidth="1"
              strokeLinecap="round"
            />
          </g>

          <g transform="rotate(6 32 17)">
            <rect x="24.5" y="7.6" width="15" height="12" rx="1.4" fill="#fff" />
            <rect
              x="24.5"
              y="7.6"
              width="15"
              height="12"
              rx="1.4"
              fill="none"
              stroke="#000"
              strokeOpacity="0.12"
            />
            <path
              d="M27.4 11.2h9.2M27.4 14h9.2M27.4 16.8h5.6"
              stroke="#9AA6B2"
              strokeWidth="1"
              strokeLinecap="round"
            />
          </g>
        </>
      )}

      <path d={open ? FRONT_OPEN : FRONT_SHUT} fill={tint ? 'currentColor' : `url(#${front})`} />

      {/* Top-edge highlight on the front face - what stops it reading flat. */}
      <path d={open ? LIP_OPEN : LIP_SHUT} fill="#fff" opacity="0.35" />
    </svg>
  );
}
